/**
 * PKCE (RFC 7636) — the two values X requires to start an OAuth 2.0 authorization-code flow.
 *
 * X supports *only* authorization code with PKCE, so unlike LinkedIn and Facebook the browser
 * has a job beyond collecting a `code`: it mints a high-entropy `code_verifier`, keeps it, and
 * sends only its SHA-256 hash (the `code_challenge`) to the authorize URL. The verifier is handed
 * to our API at exchange time. An attacker who intercepts the redirect gets a code they cannot
 * spend, because X has pinned that code to a hash they cannot reverse.
 *
 * `S256` rather than `plain`: `plain` sends the verifier itself as the challenge, which makes the
 * whole exchange pointless. X accepts both, and only one of them is worth implementing.
 *
 * Both helpers use `crypto.subtle`, which browsers expose only on secure origins. That is not a
 * constraint in practice — the login page is HTTPS-only everywhere it runs, including `localhost`,
 * which counts as secure.
 */

/** Base64url per RFC 7636 §A: standard base64 with `+/` swapped for `-_` and no padding. */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * A fresh `code_verifier`.
 *
 * 32 random bytes become 43 base64url characters — the SHORTEST string RFC 7636 allows (the range
 * is 43–128). That is deliberate rather than lazy: the entropy is what matters, the length is
 * only its encoding, and a longer string buys nothing while giving more to store and pass around.
 */
export function createCodeVerifier(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

/** The `code_challenge` for a verifier: base64url(SHA-256(verifier)). */
export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}
